import React from "react";
import { Button } from "@/components/ui/button";
import { WarningCircle, ArrowClockwise, House } from "@phosphor-icons/react";

/**
 * Route-level error boundary.
 *
 * Wraps the routed page content and renders a friendly retry card if any child
 * component throws during render / lifecycle. Reset happens on navigation because
 * Layout re-mounts this component with a new `key={location.pathname}` whenever
 * the URL changes.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Log for the console + any future error tracker hook-up.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  handleRetry = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        className="min-h-[60vh] flex items-center justify-center px-4"
        data-testid="route-error-boundary"
      >
        <div className="max-w-md w-full rounded-2xl border border-black/5 dark:border-white/10 bg-card p-8 text-center shadow-sm">
          <div
            className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-rose-500/10 text-rose-500 mb-4"
            aria-hidden="true"
          >
            <WarningCircle size={28} weight="duotone" />
          </div>
          <h1 className="font-heading text-xl font-semibold tracking-tight">
            Something went wrong on this page
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            The rest of the app is still working. Try reloading this view, or
            head back to the dashboard.
          </p>
          {process.env.NODE_ENV !== "production" && (
            <pre
              className="mt-4 p-3 text-left text-[11px] leading-relaxed bg-black/5 dark:bg-white/5 rounded-lg overflow-x-auto max-h-40"
              data-testid="route-error-boundary-details"
            >
              {String(error?.message || error)}
            </pre>
          )}
          <div className="mt-6 flex items-center justify-center gap-2">
            <Button
              onClick={this.handleRetry}
              className="rounded-full bg-[#007AFF] hover:bg-[#0069d9] text-white"
              data-testid="route-error-boundary-retry"
            >
              <ArrowClockwise size={14} className="mr-1.5" weight="bold" />
              Try again
            </Button>
            <Button
              variant="ghost"
              className="rounded-full"
              onClick={() => {
                window.location.href = "/dashboard";
              }}
              data-testid="route-error-boundary-home"
            >
              <House size={14} className="mr-1.5" weight="duotone" />
              Go to dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
